import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  anexosApi,
  formatarTamanho,
  TIPOS_ANEXO,
  type Anexo,
  type DonoAnexo,
} from '@/lib/api/anexos';
import { useAuthStore } from '@/lib/stores/authStore';
import { toast } from '@/lib/stores/toastStore';

interface Props {
  donoTipo: DonoAnexo;
  donoId: string;
  /** Classificação sugerida ao abrir. */
  tipoPadrao?: string;
  titulo?: string;
  /** Explica para que servem os arquivos neste contexto. */
  ajuda?: string;
  /**
   * Rota alternativa de envio, quando o dono tem uma rota própria com RBAC
   * próprio (é o caso da evidência de visita). Só o POST muda: listar, baixar
   * e remover seguem pelas rotas de anexo.
   */
  rotaEnvio?: string;
  /**
   * Avisa quem chamou que um arquivo entrou. Existe para o Perfil registrar o
   * anexo no diálogo com o OTO — e só o fato de ter entrado: o componente não
   * lê o arquivo, então não há o que informar além do metadado.
   */
  aoAnexar?: (anexo: Anexo) => void;
}

const ICONE: Array<[RegExp, string]> = [
  [/^application\/pdf/, '📄'],
  [/^image\//, '🖼️'],
  [/^audio\//, '🎧'],
  [/^text\//, '📃'],
];

const iconeDe = (mime: string) => ICONE.find(([re]) => re.test(mime))?.[1] ?? '📎';

/**
 * Lista e envio de anexos de um registro.
 *
 * Componente único para solicitação, contato e visita: os três guardam arquivo
 * pelo mesmo motivo (sustentar o que o registro afirma) e com as mesmas regras
 * de tamanho e tipo. Telas separadas divergiriam na primeira mudança.
 */
export function Anexos({
  donoTipo,
  donoId,
  tipoPadrao = 'Outro',
  titulo = 'Anexos',
  ajuda,
  rotaEnvio,
  aoAnexar,
}: Props) {
  const token = useAuthStore((s) => s.token) || '';
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [tipo, setTipo] = useState(tipoPadrao);

  const chave = ['anexos', donoTipo, donoId];

  const { data: anexos = [], isLoading } = useQuery({
    queryKey: chave,
    queryFn: () => anexosApi.list(donoTipo, donoId, token),
    enabled: !!token && !!donoId,
  });

  const enviar = useMutation({
    mutationFn: (arquivo: File) =>
      anexosApi.upload(donoTipo, donoId, arquivo, tipo, token, { rota: rotaEnvio }),
    onSuccess: (a) => {
      queryClient.invalidateQueries({ queryKey: chave });
      toast(`"${a.nome}" anexado (${formatarTamanho(a.tamanho)}).`);
      aoAnexar?.(a);
    },
    onError: (err: any) => {
      // O erro pode vir da API (com corpo) ou da checagem local de tamanho
      // (Error comum). Ler só um dos dois deixaria metade dos casos mudos.
      const msg = err?.response?.data?.message;
      toast(Array.isArray(msg) ? msg.join(' · ') : msg || err?.message || 'Falha ao anexar.');
    },
    onSettled: () => {
      // Limpa o input para que escolher o mesmo arquivo de novo dispare o
      // evento: o navegador não emite change quando o valor não muda.
      if (inputRef.current) inputRef.current.value = '';
    },
  });

  const remover = useMutation({
    mutationFn: (id: string) => anexosApi.remover(id, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chave });
      toast('Anexo removido.');
    },
    onError: (err: any) =>
      toast(err?.response?.data?.message || 'Não foi possível remover o anexo.'),
  });

  const baixar = async (a: Anexo) => {
    try {
      await anexosApi.baixar(a, token);
    } catch {
      toast('Não foi possível baixar o arquivo.');
    }
  };

  return (
    <div className="card flat" style={{ marginTop: 16 }}>
      <h3 style={{ marginBottom: 4 }}>
        {titulo} {anexos.length > 0 && <span className="chip neutral">{anexos.length}</span>}
      </h3>
      {ajuda && (
        <div style={{ fontSize: 11, color: 'var(--ink3)', marginBottom: 10 }}>{ajuda}</div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <select className="sel" value={tipo} onChange={(e) => setTipo(e.target.value)}>
          {TIPOS_ANEXO.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>

        <input
          ref={inputRef}
          type="file"
          style={{ display: 'none' }}
          accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.mp3,.ogg,.m4a,.txt,.csv,.docx,.xlsx"
          onChange={(e) => {
            const arquivo = e.target.files?.[0];
            if (arquivo) enviar.mutate(arquivo);
          }}
        />
        <button
          className="btn"
          onClick={() => inputRef.current?.click()}
          disabled={enviar.isPending}
        >
          {enviar.isPending ? 'Enviando...' : '+ Anexar arquivo'}
        </button>

        <span style={{ fontSize: 11, color: 'var(--ink3)' }}>
          Até 3 MB por arquivo. Fotos grandes são reduzidas automaticamente.
        </span>
      </div>

      <div style={{ marginTop: 12 }}>
        {isLoading && <div className="empty">Carregando anexos...</div>}

        {!isLoading && anexos.length === 0 && (
          <div className="empty" style={{ fontSize: 12 }}>
            Nenhum arquivo anexado.
          </div>
        )}

        {anexos.map((a) => (
          <div
            key={a.id}
            className="row"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 0',
              borderBottom: '1px solid var(--line)',
            }}
          >
            <span style={{ fontSize: 18 }}>{iconeDe(a.mime)}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13, wordBreak: 'break-all' }}>{a.nome}</div>
              <div style={{ fontSize: 11, color: 'var(--ink3)' }}>
                {a.tipo} · {formatarTamanho(a.tamanho)} ·{' '}
                {new Date(a.created_at).toLocaleDateString('pt-BR')}
              </div>
            </div>
            <button className="btn sm" onClick={() => baixar(a)}>
              Baixar
            </button>
            <button
              className="btn sm"
              disabled={remover.isPending}
              onClick={() => {
                if (window.confirm(`Remover "${a.nome}"?\n\nO arquivo é apagado do banco e não há cópia em outro lugar. A remoção fica na trilha de auditoria.`)) {
                  remover.mutate(a.id);
                }
              }}
            >
              Remover
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
